import React, {useState} from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import classnames from 'classnames';
import {debounce} from 'lodash';

import {getModelForResourceType} from 'resources/modelRegistry';
import {normalizeSearchString} from 'lib/stringUtils';

import {SearchInput, TabNavigation, Text} from '@albert-io/atomic';

import IndiePracticePanel from '../IndiePracticePanel/IndiePracticePanel.react';

import IndiePracticeTab from './IndiePracticeTab/IndiePracticeTab.react';

import './indie-practice-tabs.scss';

function findMatchingSubjects(subjects, curriculumArea = null, searchString = '') {
  return subjects
    .filter((subject) =>
      curriculumArea ? subject.getCurriculumArea().getId() === curriculumArea : subject
    )
    .filter((subject) =>
      searchString.length
        ? normalizeSearchString(subject.getName()).indexOf(searchString) > -1
        : subject
    );
}

export default function IndiePracticeTabs({curriculumAreas, subjects}) {
  const [activeTab, setActiveTab] = useState('all');
  const [searchStr, setSearchStr] = useState('');

  const onSearchChange = debounce((e) => {
    const str = normalizeSearchString(e.target.value);
    setActiveTab('all');
    setSearchStr(str);
  }, 500);

  const activeCurriculumArea = activeTab === 'all' ? null : activeTab;
  const subjectsToShow = findMatchingSubjects(subjects, activeCurriculumArea, searchStr);

  return (
    <>
      <SearchInput
        aria-label='Search subjects by title'
        onChange={(e) => {
          e.persist();
          onSearchChange(e);
        }}
        className='ip__searchbox'
        onReset={() => setSearchStr('')}
        placeholder='Search subjects'
      />
      <TabNavigation
        align='left'
        as='ul'
        className='u-list-style_none u-pad_0 u-width_100pc'
        paddingLevel={0}
        role='tablist'
      >
        <TabNavigation.Tab
          as='li'
          className='u-display_flex ip__tab'
          isActive={activeTab === 'all'}
          key='all'
          role='presentation'
        >
          <a
            aria-selected={activeTab === 'all'}
            className={classnames('u-text-decoration_none u-pad-tb_3 u-pad-lr_2 ip__tab__link', {
              'u-color_blue-400': activeTab === 'all',
              'ip__tab__link--inactive': activeTab !== 'all'
            })}
            href='#sectionall'
            id='all'
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('all');
            }}
            role='tab'
          >
            All
          </a>
        </TabNavigation.Tab>
        {curriculumAreas.map((ca) => {
          return (
            <IndiePracticeTab
              curriculumArea={ca}
              isActive={activeTab === ca.getId()}
              key={ca.getId()}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(ca.getId());
              }}
            />
          );
        })}
      </TabNavigation>
      {searchStr && subjectsToShow.isEmpty() ? (
        <Text as='p' className='u-mar-tb_4 u-width_100pc' emphasis='lowest'>
          We did not find any subjects open for independent practice with &quot;<b>{searchStr}</b>
          &quot;.
        </Text>
      ) : null}
      {searchStr && !subjectsToShow.isEmpty() ? (
        <Text as='p' className='u-mar-tb_4 u-width_100pc' emphasis='lowest'>
          Results for &quot;<b>{searchStr}</b>&quot;
        </Text>
      ) : null}
      <IndiePracticePanel
        aria-labelledby='all'
        hidden={activeTab !== 'all'}
        id='sectionall'
        key='sectionall'
        role='tabpanel'
        subjects={activeTab === 'all' ? subjectsToShow : subjects}
      />
      {curriculumAreas.map((ca) => {
        const isHidden = activeTab !== ca.getId();
        return (
          <IndiePracticePanel
            aria-labelledby={ca.getId()}
            hidden={isHidden}
            id={`section${ca.getId()}`}
            key={`section${ca.getId()}`}
            role='tabpanel'
            subjects={!isHidden ? subjectsToShow : findMatchingSubjects(subjects, ca.getId())}
          />
        );
      })}
    </>
  );
}

IndiePracticeTabs.propTypes = {
  curriculumAreas: ImmutablePropTypes.listOf(
    PropTypes.instanceOf(getModelForResourceType('curriculum_area_v1'))
  ),
  subjects: ImmutablePropTypes.listOf(PropTypes.instanceOf(getModelForResourceType('subject_v2')))
};
